import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { ClassRoom, Student, TimetableSlot } from '@/lib/types';
import { DAY_NAMES } from '@/lib/constants';
import Modal from '@/components/base/Modal';
import StudentRosterPicker from '@/components/feature/StudentRosterPicker';

type Props = {
  open: boolean;
  slot: TimetableSlot | null;
  classRoom: ClassRoom | null;
  students: Student[];
  classStudentIds: string[];
  slotStudentIds: string[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
};

export default function SlotFormModal({
  open,
  slot,
  classRoom,
  students,
  classStudentIds,
  slotStudentIds,
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const lastSlotId = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !slot) {
      lastSlotId.current = null;
      return;
    }
    if (lastSlotId.current === slot.id) return;
    lastSlotId.current = slot.id;
    setDayOfWeek(slot.day_of_week);
    setStartTime(slot.start_time?.slice(0, 5) || '09:00');
    setEndTime(slot.end_time?.slice(0, 5) || '10:00');
    // 항목에 별도 명단이 있으면 그 명단을, 없으면 연결된 반 명단을 체크해 둬요.
    setSelectedIds(new Set(slot.students_customized ? slotStudentIds : classStudentIds));
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot]);

  function toggleStudent(studentId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!slot) return;
    if (endTime <= startTime) {
      setFormError('종료 시간은 시작 시간보다 늦어야 해요.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const { error: updateError } = await supabase
        .from('timetable')
        .update({
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
          students_customized: true,
        })
        .eq('id', slot.id);
      if (updateError) throw updateError;

      const { error: deleteError } = await supabase
        .from('timetable_students')
        .delete()
        .eq('slot_id', slot.id);
      if (deleteError) throw deleteError;

      if (selectedIds.size > 0) {
        const rows = Array.from(selectedIds).map((studentId) => ({
          slot_id: slot.id,
          student_id: studentId,
        }));
        const { error: insertError } = await supabase.from('timetable_students').insert(rows);
        if (insertError) throw insertError;
      }
      onSaved();
    } catch (err) {
      setFormError('저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!slot) return;
    setSaving(true);
    setFormError('');
    try {
      const { error } = await supabase.from('timetable').delete().eq('id', slot.id);
      if (error) throw error;
      onDeleted();
    } catch (err) {
      setFormError('삭제에 실패했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200';

  return (
    <Modal open={open} title="수업 편집" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            반 / 수업
          </label>
          <div className="flex items-center gap-2 rounded-md bg-background-50 px-3 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
              {(classRoom?.name || '?').charAt(0)}
            </span>
            <span className="text-sm font-medium text-foreground-900">
              {classRoom?.name || '삭제된 반'}
            </span>
            {classRoom?.subject && (
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700">
                {classRoom.subject}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">요일</label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              className={inputClass}
            >
              {DAY_NAMES.map((d, i) => (
                <option key={i} value={i}>
                  {d}요일
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">시작</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">종료</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-foreground-700">학생 명단</label>
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              {selectedIds.size}명
            </span>
          </div>
          <p className="mb-2 mt-1 text-xs text-foreground-400">
            체크해서 이 수업에 들어갈 학생을 넣고 뺄 수 있어요. 여기서 바꾼 명단은 이 수업에만
            적용돼요.
          </p>
          <div className="rounded-md border border-background-200 p-1">
            <StudentRosterPicker
              students={students}
              selectedIds={selectedIds}
              onToggle={toggleStudent}
            />
          </div>
        </div>

        {formError && (
          <div className="rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
            {formError}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="cursor-pointer whitespace-nowrap rounded-md border border-accent-300 px-4 py-2 text-sm font-medium text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            삭제
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}